import { findCodeRanges, inRanges } from './codeRanges.js';
import type { Frontmatter } from './frontmatter.js';
import { nfc, normKey } from './text.js';

export type LinkKind = 'text' | 'property' | 'embed';

export interface WikilinkParts {
  target: string;
  heading: string | null;
  alias: string | null;
}

export interface LinkOccurrence extends WikilinkParts {
  /** Offset des ersten Zeichens (inkl. `!`). */
  start: number;
  /** Offset nach dem letzten Zeichen. */
  end: number;
  embed: boolean;
  syntax: 'wiki' | 'markdown';
  /** Bei Bild-Einbettungen `![[Bild.png|300]]` die Breite in Pixeln. */
  width: number | null;
  /** `|` oder `\|` (in Tabellen escaped). */
  aliasSeparator: string;
}

const WIKI_RE = /(!?)\[\[([^[\]\r\n]+?)\]\]/g;
const MD_LINK_RE = /(!?)\[((?:[^[\]\r\n]|\[[^\]\r\n]*\])*)\]\(\s*(<[^>\r\n]*>|[^)\s]+)(?:\s+(?:"[^"\r\n]*"|'[^'\r\n]*'))?\s*\)/g;
const SCHEME_RE = /^[a-z][a-z0-9+.-]*:/i;

/** Schlüssel, unter dem die API aufgelöste Link-Ziele liefert. */
export function linkKey(target: string): string {
  return normKey(target.trim());
}

/** Zerlegt den Inhalt zwischen `[[` und `]]`. */
export function parseWikilinkInner(inner: string): WikilinkParts & { aliasSeparator: string } {
  let aliasSeparator = '|';
  let targetPart = inner;
  let alias: string | null = null;
  const esc = inner.indexOf('\\|');
  const pipe = inner.indexOf('|');
  if (esc !== -1 && esc + 1 === pipe) {
    aliasSeparator = '\\|';
    targetPart = inner.slice(0, esc);
    alias = inner.slice(esc + 2);
  } else if (pipe !== -1) {
    targetPart = inner.slice(0, pipe);
    alias = inner.slice(pipe + 1);
  }
  let heading: string | null = null;
  const hash = targetPart.indexOf('#');
  if (hash !== -1) {
    heading = targetPart.slice(hash + 1).trim() || null;
    targetPart = targetPart.slice(0, hash);
  }
  return { target: nfc(targetPart.trim()), heading, alias: alias === null ? null : alias.trim(), aliasSeparator };
}

function parseWidth(alias: string | null): number | null {
  if (!alias) return null;
  const m = /^(\d{1,5})(?:x\d{1,5})?$/.exec(alias.trim());
  return m ? Number(m[1]) : null;
}

/** Findet alle Wikilinks (`[[…]]`, `![[…]]`) außerhalb von Code. */
export function scanWikilinks(md: string, code = findCodeRanges(md)): LinkOccurrence[] {
  const out: LinkOccurrence[] = [];
  for (const m of md.matchAll(WIKI_RE)) {
    const start = m.index;
    const bracket = start + m[1]!.length;
    if (inRanges(code, bracket)) continue;
    const parts = parseWikilinkInner(m[2]!);
    if (!parts.target && !parts.heading) continue;
    const embed = m[1] === '!';
    out.push({
      ...parts,
      start,
      end: start + m[0].length,
      embed,
      syntax: 'wiki',
      width: embed ? parseWidth(parts.alias) : null,
    });
  }
  return out;
}

/**
 * Findet Markdown-Links/-Bilder mit lokalem Ziel: `![alt](Bilder/x.png)` und `[Text](Notiz.md)`.
 * Externe URLs und reine Anker werden ignoriert.
 */
export function scanMarkdownLinks(md: string, code = findCodeRanges(md)): LinkOccurrence[] {
  const out: LinkOccurrence[] = [];
  for (const m of md.matchAll(MD_LINK_RE)) {
    const start = m.index;
    if (inRanges(code, start + m[1]!.length)) continue;
    // `[[x]](…)` ist kein Markdown-Link im Obsidian-Sinn
    if (m[2]!.startsWith('[') && m[2]!.endsWith(']')) continue;
    let dest = m[3]!;
    if (dest.startsWith('<')) dest = dest.slice(1, -1);
    if (!dest || SCHEME_RE.test(dest) || dest.startsWith('#') || dest.startsWith('//')) continue;
    let decoded: string;
    try {
      decoded = decodeURIComponent(dest);
    } catch {
      decoded = dest;
    }
    const embed = m[1] === '!';
    let heading: string | null = null;
    const hash = decoded.indexOf('#');
    if (hash !== -1) {
      heading = decoded.slice(hash + 1) || null;
      decoded = decoded.slice(0, hash);
    }
    // Nicht-Bild-Links nur, wenn sie auf eine Markdown-Datei zeigen
    if (!embed && !/\.md$/i.test(decoded)) continue;
    out.push({
      target: nfc(decoded),
      heading,
      alias: m[2]! || null,
      start,
      end: start + m[0].length,
      embed,
      syntax: 'markdown',
      width: null,
      aliasSeparator: '|',
    });
  }
  return out;
}

/** Alle Link-Vorkommen, nach Position sortiert. */
export function scanAllLinks(md: string): LinkOccurrence[] {
  const code = findCodeRanges(md);
  const wiki = scanWikilinks(md, code);
  const mdl = scanMarkdownLinks(md, code).filter((l) => !wiki.some((w) => l.start < w.end && w.start < l.end));
  return [...wiki, ...mdl].sort((a, b) => a.start - b.start);
}

export interface ExtractedLink {
  rawTarget: string;
  kind: LinkKind;
  heading: string | null;
  /** Bei `property`: oberster Frontmatter-Schlüssel, z. B. `leader`. */
  property: string | null;
}

/**
 * Extrahiert die zu speichernden Links einer Notiz aus Body und Frontmatter.
 * Duplikate (gleiches Ziel, gleiche Art, gleiche Überschrift) werden zusammengefasst.
 * Reine Selbstverweise `[[#Abschnitt]]` werden ignoriert.
 */
export function extractLinks(body: string, frontmatter: Frontmatter = {}): ExtractedLink[] {
  const seen = new Set<string>();
  const out: ExtractedLink[] = [];
  const add = (rawTarget: string, kind: LinkKind, heading: string | null, property: string | null = null) => {
    if (!rawTarget) return;
    const key = `${kind}\u0000${normKey(rawTarget)}\u0000${heading ?? ''}\u0000${property ?? ''}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ rawTarget, kind, heading, property });
  };
  for (const occ of scanAllLinks(body)) add(occ.target, occ.embed ? 'embed' : 'text', occ.heading);
  for (const [prop, value] of Object.entries(frontmatter ?? {})) {
    for (const s of frontmatterStrings(value)) {
      for (const occ of scanWikilinks(s, [])) add(occ.target, 'property', occ.heading, prop);
    }
  }
  return out;
}

/** Alle String-Werte eines Frontmatter-Objekts (rekursiv). */
export function frontmatterStrings(v: unknown): string[] {
  if (typeof v === 'string') return [v];
  if (Array.isArray(v)) return v.flatMap(frontmatterStrings);
  if (v && typeof v === 'object') return Object.values(v).flatMap(frontmatterStrings);
  return [];
}

/** Baut einen Wikilink wieder zusammen. */
export function formatWikilink(parts: WikilinkParts & { embed?: boolean; aliasSeparator?: string }): string {
  const h = parts.heading ? `#${parts.heading}` : '';
  const a = parts.alias !== null && parts.alias !== undefined ? `${parts.aliasSeparator ?? '|'}${parts.alias}` : '';
  return `${parts.embed ? '!' : ''}[[${parts.target}${h}${a}]]`;
}

function formatMarkdownLink(occ: LinkOccurrence, newTarget: string): string {
  const path = /\.md$/i.test(occ.target) && !/\.md$/i.test(newTarget) ? `${newTarget}.md` : newTarget;
  const dest = path + (occ.heading ? `#${occ.heading}` : '');
  const destText = /[\s()<>]/.test(dest) ? `<${dest}>` : dest;
  return `${occ.embed ? '!' : ''}[${occ.alias ?? ''}](${destText})`;
}

/**
 * Ersetzt Link-Ziele im Markdown. `fn` liefert das neue Ziel oder null (unverändert).
 * Überschrift und Alias bleiben erhalten.
 */
export function rewriteLinks(md: string, fn: (occ: LinkOccurrence) => string | null): string {
  const occs = scanAllLinks(md);
  if (occs.length === 0) return md;
  let out = '';
  let last = 0;
  for (const occ of occs) {
    const nt = fn(occ);
    if (nt === null || nt === occ.target) continue;
    out += md.slice(last, occ.start);
    out += occ.syntax === 'wiki' ? formatWikilink({ ...occ, target: nt }) : formatMarkdownLink(occ, nt);
    last = occ.end;
  }
  return last === 0 ? md : out + md.slice(last);
}

/** Ersetzt Wikilink-Ziele in allen String-Werten eines Frontmatter-Objekts. */
export function rewriteFrontmatterLinks<T>(v: T, fn: (occ: LinkOccurrence) => string | null): T {
  if (typeof v === 'string') {
    const occs = scanWikilinks(v, []);
    if (occs.length === 0) return v;
    let out = '';
    let last = 0;
    for (const occ of occs) {
      const nt = fn(occ);
      if (nt === null || nt === occ.target) continue;
      out += v.slice(last, occ.start) + formatWikilink({ ...occ, target: nt });
      last = occ.end;
    }
    return (last === 0 ? v : out + v.slice(last)) as T;
  }
  if (Array.isArray(v)) return v.map((x) => rewriteFrontmatterLinks(x, fn)) as T;
  if (v && typeof v === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, val] of Object.entries(v)) out[k] = rewriteFrontmatterLinks(val, fn);
    return out as T;
  }
  return v;
}
